import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertCircle, Sparkles, Zap, Calendar, Camera, ShieldCheck, Rocket, Palette, Globe, Smartphone, Headphones, RefreshCw, BarChart3, Users, ExternalLink, ShoppingCart, CalendarCheck, Database } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_CALENDLY_URL = "https://calendly.com/hey-wedowedo/30min";

// Default portfolio references (always shown on the booking page)
const DEFAULT_REFERENCES = [
  { title: "Toolora", url: "https://toolora.se", description: "Komplett affärssystem för måleriföretag — leadgenerering, CRM, offert, tidsrapportering & Fortnox-integration." },
  { title: "Seravasports.com", url: "https://seravasports.com", description: "Heltäckande e-handel för premiumpaket — egen produkt-builder, CRM för komplexa ordrar och skräddarsydd kund-admin." },
  { title: "FlowFocus.se", url: "https://flowfocus.se", description: "Task management-app skräddarsydd för ADHD — fokus, struktur och visuell tydlighet." },
  { title: "Biro", url: "https://biro.se", description: "Plattform för företagstjänster med snabb onboarding och automatiserade flöden." },
  { title: "Enduroschool.com", url: "https://enduroschool.com", description: "Bokningssystem och hemsida för enduroskola — kurser, evenemang och betalning på samma plattform." },
  { title: "Arcticriders.com", url: "https://arcticriders.com", description: "Action-sport-varumärke med integrerad e-handel, bokningar och content-driven marknadsföring." },
];

export default function OrderPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/order/${token}`)
      .then(r => setData(r.data))
      .catch(() => setError("Sidan hittades inte"))
      .finally(() => setLoading(false));
  }, [token]);

  // Lazy load Calendly inline embed script
  useEffect(() => {
    if (!data) return;
    if (document.querySelector('script[src*="assets.calendly.com/assets/external/widget.js"]')) {
      setCalendlyLoaded(true);
      return;
    }
    const s = document.createElement("script");
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    s.onload = () => setCalendlyLoaded(true);
    document.body.appendChild(s);
    if (!document.querySelector('link[href*="assets.calendly.com/assets/external/widget.css"]')) {
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(l);
    }
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#0A8294] animate-spin" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-10 h-10 text-[#F59073] mx-auto mb-3" />
          <p className="text-[#0A1628] text-lg mb-1">{error || "Sidan hittades inte"}</p>
          <p className="text-[#6B7280] text-sm">Kontrollera länken eller kontakta oss på hey@wedowedo.co</p>
        </div>
      </div>
    );
  }

  const calendlyUrl = data.calendly_url || DEFAULT_CALENDLY_URL;
  const currency = data.currency || "SEK";
  const fmtPrice = (n) => `${(Number(n) || 0).toLocaleString("sv-SE")} ${currency}`;
  const customExtras = Array.isArray(data.custom_extras) ? data.custom_extras : [];

  // Merge per-outreach references with default portfolio references (dedupe by URL)
  const outreachRefs = (Array.isArray(data.references) ? data.references : []).filter(r => r && r.url);
  const seen = new Set(outreachRefs.map(r => (r.url || "").replace(/\/+$/, "").toLowerCase()));
  const mergedRefs = [...outreachRefs, ...DEFAULT_REFERENCES.filter(r => !seen.has((r.url || "").replace(/\/+$/, "").toLowerCase()))];

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0A1628] relative overflow-hidden" data-testid="proposal-page">
      {/* Futuristic ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-[#0A8294]/10 rounded-full blur-[120px]" />
        <div className="absolute top-1/3 -right-24 w-[420px] h-[420px] bg-[#F59073]/15 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-1/3 w-[380px] h-[380px] bg-[#9333EA]/8 rounded-full blur-[140px]" />
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{ backgroundImage: "radial-gradient(circle, #0A1628 1px, transparent 1px)", backgroundSize: "32px 32px" }}
        />
      </div>

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-20">

        {/* Header */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-full bg-white/70 backdrop-blur-sm border border-[#0A8294]/20 shadow-sm">
            <Sparkles className="w-3 h-3 text-[#0A8294]" />
            <span className="text-[10px] font-mono text-[#0A8294] tracking-[0.2em] uppercase">FÖRSLAG · WEDOWEDO</span>
          </div>
          <h1 className="font-outfit font-bold text-4xl sm:text-6xl mb-4 leading-[1.05] tracking-tight">
            Hej {data.to_name || "där"},<br />
            <span className="bg-gradient-to-r from-[#0A8294] via-[#0A1628] to-[#F59073] bg-clip-text text-transparent">här är ert förslag</span>
          </h1>
          <p className="text-[#475569] text-lg leading-relaxed max-w-2xl">
            En personlig genomgång av vad vi kan bygga åt {data.company_name || "er"} — och hur vi kan ha allt live på 48 timmar.
          </p>
        </div>

        {/* 48h badge */}
        <div className="inline-flex items-center gap-2.5 bg-gradient-to-r from-[#0A8294]/10 to-[#F59073]/10 border border-[#0A8294]/30 rounded-full px-5 py-2.5 mb-12 shadow-sm" data-testid="48h-badge">
          <div className="relative">
            <Zap className="w-4 h-4 text-[#0A8294]" />
            <div className="absolute inset-0 bg-[#0A8294]/30 blur-md rounded-full" />
          </div>
          <span className="text-sm text-[#0A1628] font-medium">Live på 48 timmar — från godkänt förslag till lansering</span>
        </div>

        {/* Personal intro text from outreach */}
        {data.intro_text && (
          <div className="bg-white/70 backdrop-blur-sm border-l-4 border-[#0A8294] rounded-r-lg px-6 py-5 mb-12 shadow-sm" data-testid="intro-text">
            <p className="text-[#334155] text-base leading-relaxed whitespace-pre-line">{data.intro_text}</p>
          </div>
        )}

        {/* Personal video */}
        {data.video_url && (
          <section className="mb-12" data-testid="intro-video-section">
            <p className="text-[10px] font-mono text-[#94A3B8] tracking-[0.2em] mb-3">PERSONLIG INTRODUKTION</p>
            <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden shadow-[0_8px_32px_-12px_rgba(10,22,40,0.15)]">
              <video controls preload="metadata" className="w-full aspect-video bg-black" data-testid="intro-video">
                <source src={data.video_url} />
                Din webbläsare stöder inte video. <a href={data.video_url} className="text-[#0A8294]">Öppna direkt →</a>
              </video>
            </div>
          </section>
        )}

        {/* Preview */}
        {data.preview_url && (
          <a href={data.preview_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-between bg-white/80 backdrop-blur-sm border border-[#E5E7EB] hover:border-[#0A8294]/40 hover:shadow-[0_8px_24px_-8px_rgba(10,130,148,0.25)] rounded-xl p-5 mb-12 group transition-all duration-300" data-testid="preview-link">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#0A8294]/15 to-[#0A8294]/5 border border-[#0A8294]/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-[#0A8294]" />
              </div>
              <div>
                <p className="text-[#0A1628] text-sm font-semibold">Se er live preview</p>
                <p className="text-[#64748B] text-xs mt-0.5">Klicka för att utforska den färdiga skissen — fullt anpassningsbar</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-[#94A3B8] group-hover:text-[#0A8294] transition-colors" />
          </a>
        )}

        {/* References */}
        {mergedRefs.length > 0 && (
          <Card label="REFERENSER · TIDIGARE ARBETE">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {mergedRefs.map((r, i) => r.url && (
                <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`reference-${i}`}
                  className="block bg-white border border-[#E5E7EB] hover:border-[#0A8294]/40 hover:shadow-md rounded-lg p-4 transition-all duration-200 group">
                  <p className="text-[#0A8294] text-sm font-semibold group-hover:underline">{r.title || r.url} →</p>
                  {r.description && <p className="text-[#64748B] text-xs mt-1 leading-relaxed">{r.description}</p>}
                </a>
              ))}
            </div>
          </Card>
        )}

        {/* About us */}
        <Card label="OM OSS · WEDOWEDO" testId="about-us-section">
          <div className="space-y-4 text-[#334155] text-[15px] leading-relaxed">
            <p>
              Vi är ett team av entreprenörer med över <span className="text-[#0A1628] font-semibold">20 års samlad erfarenhet</span> av
              att bygga, driva och sälja företag i olika branscher och storlekar. Vi har själva grundat och vuxit bolag som{" "}
              <span className="text-[#0A1628] font-semibold">omsatt över 100 miljoner SEK</span> — och framgångsrikt avyttrat dem.
              Den resan har gett oss djup operativ insikt i allt från personal och företagsutveckling till skalning, säljprocesser
              och vad som faktiskt krävs för att lyckas i praktiken.
            </p>
            <p>
              Idag fokuserar vi fullt ut på{" "}
              <span className="bg-gradient-to-r from-[#0A8294] to-[#0EA5BD] bg-clip-text text-transparent font-semibold">AI</span>{" "}
              — ett område där vi ser hur snabbt spelplanen förändras. Det som tidigare krävde stora resurser och långa ledtider kan idag
              effektiviseras, automatiseras och förbättras till en bråkdel av kostnaden. Vårt fokus ligger på att bygga{" "}
              <span className="text-[#0A1628] font-semibold">smarta, skalbara AI-drivna arbetsflöden</span> som skapar verkligt värde i verksamheter.
            </p>
            <p>
              Vi är hungriga, framåtlutade och bygger kontinuerligt egna lösningar — vilket också speglas i de case och referenser vi delar.
            </p>
          </div>
        </Card>

        {/* What's possible */}
        <Card label="VAD VI KAN BYGGA">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
            <Possibility icon={Rocket} title="Modern hemsida" items={["Snabb, mobilanpassad design", "Konverteringsoptimerad", "SEO-grund i kärnan"]} />
            <Possibility icon={Palette} title="Skräddarsydd design" items={["Anpassat efter ert varumärke", "Ni godkänner i varje steg", "Inget mall-tjafs"]} />
            <Possibility icon={Database} title="CRM-system" items={["Skräddarsytt efter er affär", "Kundkort, ärenden & noteringar", "Integration med hemsidan"]} />
            <Possibility icon={CalendarCheck} title="Bokningssystem" items={["Tider, kurser eller resurser", "Betalning vid bokning", "Automatiska bekräftelsemejl"]} />
            <Possibility icon={ShoppingCart} title="E-handel" items={["Stripe & Klarna inbyggt", "Produkter, paket & rabatter", "Egen admin för full kontroll"]} />
            <Possibility icon={Globe} title="Hosting & lansering" items={["Egen domän", "Säkerhet & SSL", "Live på 48h från start"]} />
            <Possibility icon={Smartphone} title="Mobilförst" items={["100% responsiv", "Optimerad för touch", "Snabba laddtider"]} />
            <Possibility icon={Headphones} title="Support & drift" items={["Hosting ingår", "Löpande uppdateringar", "Svar inom 24h (vardagar)"]} />
            <Possibility icon={RefreshCw} title="Vidareutveckling" items={["Funktioner kan läggas till", "Vi växer med er", "Långsiktig partner"]} />
          </div>
        </Card>

        {/* Optional add-ons */}
        {(data.photo_package_offer || data.admin_panel_offer || data.crm_offer || customExtras.length > 0) && (
          <Card label="VALFRIA TILLVAL" testId="addons-section">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.photo_package_offer && (
                <AddonCard icon={Camera} title="Fotopaket" desc={data.photo_description || "Professionell fotografering — vi kommer ut till er och tar bilder anpassade för hemsidan."} />
              )}
              {data.admin_panel_offer && (
                <AddonCard icon={ShieldCheck} title="Adminpanel" desc={data.admin_panel_description || "Egen adminpanel där ni själva hanterar innehåll, bokningar eller annan data."} />
              )}
              {data.crm_offer && (
                <AddonCard icon={Users} title="CRM-system" desc={data.crm_description || "Skräddarsytt CRM-system som integreras med er hemsida och affärsflöde."} />
              )}
              {customExtras.map((ex, i) => (
                <AddonCard key={i} icon={BarChart3} title={ex.title || "Tillval"} desc={ex.description || ""} />
              ))}
            </div>
          </Card>
        )}

        {/* Pricing */}
        <Card label="INVESTERING" testId="pricing-section">
          <p className="text-[#0A1628] text-base mb-4">Två delar: en engångskostnad för hemsidan och ett löpande hosting- & supportabonnemang.</p>

          {/* One-time build fee (if provided) */}
          {Number(data.build_price ?? data.price_setup ?? 0) > 0 && (
            <div className="bg-gradient-to-br from-white to-[#F8FAFC] border border-[#E5E7EB] rounded-xl p-6 max-w-md shadow-sm mb-4" data-testid="build-price-card">
              <p className="text-[10px] font-mono text-[#94A3B8] tracking-[0.2em] mb-2">HEMSIDA · ENGÅNGSKOSTNAD</p>
              <p className="text-[#0A1628] text-3xl font-bold mb-1 tracking-tight">
                {fmtPrice(data.build_price ?? data.price_setup)}
                <span className="text-base text-[#94A3B8] font-normal"> engång</span>
              </p>
              <p className="text-[#64748B] text-xs">design, utveckling, launch</p>
            </div>
          )}

          {/* Recurring hosting — monthly + yearly side by side */}
          <p className="text-[10px] font-mono text-[#94A3B8] tracking-[0.2em] mb-3">HOSTING, SUPPORT &amp; UPPDATERINGAR</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 shadow-sm" data-testid="hosting-monthly-card">
              <p className="text-[10px] font-mono text-[#94A3B8] tracking-[0.2em] mb-1">MÅNADSVIS</p>
              <p className="text-[#0A1628] text-2xl font-bold mb-1 tracking-tight">
                {fmtPrice(data.price_monthly || 390)}
                <span className="text-sm text-[#94A3B8] font-normal">/mån</span>
              </p>
              <p className="text-[#64748B] text-xs">Ingen bindningstid, faktura varje månad</p>
            </div>
            <div className="bg-gradient-to-br from-[#0A8294]/5 to-white border border-[#0A8294]/40 rounded-xl p-5 shadow-sm relative" data-testid="hosting-yearly-card">
              <span className="absolute -top-2 right-3 text-[9px] font-mono bg-[#0A8294] text-white px-2 py-0.5 rounded-full">SPARA 2 MÅN</span>
              <p className="text-[10px] font-mono text-[#0A8294] tracking-[0.2em] mb-1">ÅRSVIS</p>
              <p className="text-[#0A1628] text-2xl font-bold mb-1 tracking-tight">
                {fmtPrice(data.price_yearly || (data.price_monthly || 390) * 10)}
                <span className="text-sm text-[#94A3B8] font-normal">/år</span>
              </p>
              <p className="text-[#64748B] text-xs">Betalas årsvis, faktura 1 gång per år</p>
            </div>
          </div>
          <p className="text-[#94A3B8] text-[10px] mt-4">Ni väljer själva månads- eller årsfakturering. Alla priser anges exkl. moms/VAT.</p>
        </Card>

        {/* Calendly booking — primary CTA */}
        <section className="relative bg-gradient-to-br from-[#0A1628] via-[#0F2440] to-[#0A1628] rounded-2xl p-6 md:p-10 mb-10 overflow-hidden shadow-[0_24px_64px_-24px_rgba(10,22,40,0.4)]" data-testid="booking-section" id="boka">
          {/* Subtle glow accents inside the booking card */}
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#0A8294]/30 rounded-full blur-[80px] pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F59073]/20 rounded-full blur-[80px] pointer-events-none" />
          <div className="relative">
            <div className="flex items-start gap-4 mb-7">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0A8294] to-[#0EA5BD] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#0A8294]/30">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-outfit font-bold text-2xl text-white mb-1">Boka 30-min genomgång</h2>
                <p className="text-[#94A3B8] text-sm leading-relaxed">Vi går igenom ert förslag tillsammans, svarar på frågor och pratar om hur vi kan ha allt klart inom 48h. Inga försäljningsknep — bara ett konstruktivt samtal.</p>
              </div>
            </div>
            <div className="bg-white rounded-xl overflow-hidden border border-white/10">
              <div
                className="calendly-inline-widget"
                data-url={`${calendlyUrl}?hide_event_type_details=0&hide_gdpr_banner=1&primary_color=0a8294&background_color=ffffff&text_color=0a1628`}
                style={{ minWidth: "320px", height: "700px" }}
                data-testid="calendly-widget"
              />
              {!calendlyLoaded && (
                <div className="flex items-center justify-center h-[700px]">
                  <Loader2 className="w-6 h-6 text-[#0A8294] animate-spin" />
                </div>
              )}
            </div>
            <p className="text-[#94A3B8] text-xs mt-4 text-center">
              Funkar inte widgeten? <a href={calendlyUrl} target="_blank" rel="noopener noreferrer" className="text-white hover:underline">Öppna bokningssidan i ny flik →</a>
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-16 pt-6 border-t border-[#E5E7EB] text-[11px] text-[#94A3B8] text-center space-y-1">
          <p>{data.seller?.name || "WeDoWeDo OÜ"} &middot; Reg. {data.seller?.reg || "17455779"} &middot; VAT {data.seller?.vat || "EE102963621"}</p>
          <p>{data.seller?.address || "Rannaniidu tn 2-1, 13516 Tallinn, Estonia"} &middot; <a href="mailto:hey@wedowedo.co" className="text-[#475569] hover:text-[#0A8294]">hey@wedowedo.co</a></p>
        </footer>
      </div>
    </div>
  );
}

function Card({ label, children, testId }) {
  return (
    <section className="bg-white/80 backdrop-blur-sm border border-[#E5E7EB] rounded-2xl p-7 md:p-9 mb-10 shadow-[0_8px_32px_-16px_rgba(10,22,40,0.08)]" data-testid={testId}>
      <p className="text-[10px] font-mono text-[#94A3B8] tracking-[0.2em] mb-5">{label}</p>
      {children}
    </section>
  );
}

function Possibility({ icon: Icon, title, items }) {
  return (
    <div>
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0A8294]/15 to-[#0A8294]/5 border border-[#0A8294]/20 flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#0A8294]" />
        </div>
        <h3 className="text-[#0A1628] font-semibold text-sm">{title}</h3>
      </div>
      <ul className="space-y-1.5 text-[#64748B] text-xs leading-relaxed pl-[42px]">
        {items.map((it, i) => <li key={i} className="relative before:absolute before:-left-3 before:top-[7px] before:w-1 before:h-1 before:rounded-full before:bg-[#0A8294]/60">{it}</li>)}
      </ul>
    </div>
  );
}

function AddonCard({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white border border-[#E5E7EB] hover:border-[#0A8294]/30 hover:shadow-md rounded-lg p-4 transition-all duration-200">
      <div className="flex items-start gap-3 mb-2">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0A8294]/15 to-[#0A8294]/5 border border-[#0A8294]/20 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-[#0A8294]" />
        </div>
        <p className="text-[#0A1628] text-sm font-semibold pt-1">{title}</p>
      </div>
      {desc && <p className="text-[#64748B] text-xs leading-relaxed pl-11">{desc}</p>}
    </div>
  );
}
